/**
 * MyTrips.jsx
 * 
 * Customer profile and booking history page.
 * Similar to Airbnb's profile page but simpler.
 * 
 * Features:
 * - Profile card with name, stats, member since
 * - Editable profile (name, photo)
 * - Booking history with tabs (upcoming, completed, cancelled)
 * - Leave review button on completed trips
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getCityImage } from '../constants/cityImages';
import { getMarketplaceCountry, setMarketplaceCountry, MARKETPLACE_COUNTRIES } from '../utils/marketplaceLocale';
import { toast } from 'sonner';
import ReviewModal from '../components/customer/ReviewModal';
import ChangeBookingModal from '../components/customer/ChangeBookingModal';
import FlowLevel from '../components/customer/FlowLevel';
import ExperiencePassport from '../components/customer/ExperiencePassport';
import { resizeImageToDataUrl } from '../utils/imageResize';
import {
  User,
  MapPin,
  Calendar,
  Star,
  Clock,
  Users,
  Edit2,
  LogOut,
  ChevronRight,
  Ticket,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Camera,
  Search,
  Globe,
  ChevronDown,
  Trash2,
  CalendarClock,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Booking card component
const BookingCard = ({ booking, onViewTicket, onReview, onChangeBooking }) => {
  const navigate = useNavigate();
  
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
    no_show: 'bg-gray-100 text-gray-800'
  };

  const statusIcons = {
    pending: AlertCircle,
    confirmed: CheckCircle,
    completed: CheckCircle,
    cancelled: XCircle,
    no_show: XCircle
  };

  const StatusIcon = statusIcons[booking.status] || AlertCircle;

  return (
    <Card className="overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Image - Larger */}
        <div className="w-full sm:w-40 h-40 flex-shrink-0 bg-gray-100">
          <img
            src={booking.service_image || 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400&h=400&fit=crop'}
            alt={booking.service_name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">
                {booking.service_name}
              </h3>
              {booking.organization && (
                booking.organization.id ? (
                  <button
                    onClick={() => navigate(`/host/${booking.organization.id}`)}
                    className="text-sm text-gray-500 hover:text-blue-600 transition-colors text-left"
                    data-testid={`trip-org-link-${booking.id}`}
                  >
                    by <span className="underline decoration-gray-300 underline-offset-2 hover:decoration-blue-400">{booking.organization.name}</span>
                  </button>
                ) : (
                  <p className="text-sm text-gray-500">by {booking.organization.name}</p>
                )
              )}
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusColors[booking.status]}`}>
              <StatusIcon className="w-3 h-3" />
              {booking.status}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {(() => {
                // Parse date string directly to avoid timezone shifting
                // booking_date comes as "2026-04-03" - we want to display it as-is
                const [year, month, day] = booking.booking_date.split('-').map(Number);
                const date = new Date(year, month - 1, day); // month is 0-indexed
                return date.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                });
              })()}
            </span>
            {booking.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {(() => {
                  const [hours, minutes] = booking.start_time.split(':').map(Number);
                  const period = hours >= 12 ? 'PM' : 'AM';
                  const hour12 = hours % 12 || 12;
                  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
                })()}
              </span>
            )}
            {(booking.city || booking.organization?.city) && (
              <span className="flex items-center gap-1 text-gray-500">
                <MapPin className="w-3 h-3" />
                {booking.city || booking.organization?.city}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {booking.party_size} {booking.party_size === 1 ? 'guest' : 'guests'}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="font-semibold text-gray-900">
              {booking.currency} {booking.total_amount?.toFixed(2)}
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs rounded-full"
                onClick={() => onViewTicket()}
                data-testid={`view-ticket-${booking.id}`}
              >
                <Ticket className="w-3 h-3 mr-1" />
                Ticket
              </Button>
              {(booking.status === 'pending' || booking.status === 'confirmed') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs rounded-full"
                  onClick={() => onChangeBooking?.(booking)}
                  data-testid={`change-booking-${booking.id}`}
                >
                  <CalendarClock className="w-3 h-3 mr-1" />
                  Change
                </Button>
              )}
              {booking.can_review && (
                <Button 
                  size="sm" 
                  className="text-xs rounded-full bg-amber-500 hover:bg-amber-600"
                  onClick={() => onReview?.(booking)}
                  data-testid={`review-booking-${booking.id}`}
                >
                  <Star className="w-3 h-3 mr-1" />
                  Review
                </Button>
              )}
              {booking.has_reviewed && (
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-xs rounded-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  onClick={() => onReview?.(booking)}
                  data-testid={`edit-review-${booking.id}`}
                >
                  <Star className="w-3 h-3 mr-1 fill-amber-400 text-amber-400" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Edit profile modal — avatar, contact details, and account deletion (2FA code)
const EditProfileModal = ({ customer, open, onClose, onSave, onDeleted, passportStamps = [] }) => {
  const [name, setName] = useState(customer?.name || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [city, setCity] = useState(customer?.city || '');
  const [bio, setBio] = useState(customer?.bio || '');
  const [avatar, setAvatar] = useState(customer?.avatar_url || '');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('form'); // 'form' | 'delete'
  const [deleteCode, setDeleteCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sentTo, setSentTo] = useState([]);
  const avatarInputRef = React.useRef(null);

  useEffect(() => {
    if (customer) {
      setName(customer.name || '');
      setPhone(customer.phone || '');
      setCity(customer.city || '');
      setBio(customer.bio || '');
      setAvatar(customer.avatar_url || '');
    }
  }, [customer]);

  useEffect(() => {
    if (open) { setMode('form'); setDeleteCode(''); setCodeSent(false); }
  }, [open]);

  const handleAvatarPick = async (file) => {
    try {
      const dataUrl = await resizeImageToDataUrl(file, 400, 0.85);
      setAvatar(dataUrl);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await onSave({ name, phone, city, bio, avatar_url: avatar });
      if (result?.success === false) {
        toast.error(result.message || 'Failed to save profile');
      } else {
        toast.success('Profile updated');
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const startDelete = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/account/delete/start`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      setSentTo(data.sent_to || []);
      setCodeSent(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/account/delete/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: deleteCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Deletion failed');
      toast.success('Your account and personal data have been deleted.');
      onDeleted?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-profile-modal">
        <DialogHeader>
          <DialogTitle>{mode === 'delete' ? 'Delete your account' : 'Edit Profile'}</DialogTitle>
        </DialogHeader>

        {mode === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {avatar ? (
                <img src={avatar} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <User className="w-7 h-7 text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700"
                data-testid="avatar-upload-btn"
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-xs text-gray-500">
              <p className="font-medium text-gray-700">Profile photo</p>
              <p>Shown next to your reviews.</p>
              {avatar && (
                <button type="button" onClick={() => setAvatar('')} className="text-red-500 hover:text-red-600 mt-0.5" data-testid="avatar-remove-btn">Remove photo</button>
              )}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) handleAvatarPick(e.target.files[0]); e.target.value = ''; }}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="profile-name-input" />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Phone</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 1234" data-testid="profile-phone-input" />
            <p className="text-[11px] text-gray-400 mt-1">Lets you sign in with a text code and helps operators reach you.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">City</label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Where are you from?" />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">About me</label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell us about yourself..." rows={3} />
          </div>

          <ExperiencePassport stamps={passportStamps} />

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" className="rounded-full" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-full" disabled={loading} data-testid="profile-save-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>

          {/* Danger zone */}
          <div className="pt-2 mt-1">
            <button
              type="button"
              onClick={() => setMode('delete')}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete my account
            </button>
          </div>
        </form>
        )}

        {mode === 'delete' && (
          <div className="space-y-4" data-testid="delete-account-flow">
            <div className="bg-red-50 rounded-xl p-3.5 text-xs text-red-700 leading-relaxed">
              This permanently removes your name, email, phone and photo, deactivates your account and signs you out everywhere. Past bookings remain with the operators (legal transaction records) but are no longer linked to you.
            </div>

            {!codeSent ? (
              <>
                <p className="text-sm text-gray-600">To protect your account, we'll send a 6-digit confirmation code to your email{customer?.phone ? ' and phone' : ''}.</p>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setMode('form')}>Back</Button>
                  <Button type="button" variant="destructive" onClick={startDelete} disabled={loading} data-testid="delete-send-code-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send confirmation code'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">Enter the code we sent to your {sentTo.join(' and ') || 'email'}:</p>
                <Input
                  value={deleteCode}
                  onChange={(e) => setDeleteCode(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  className="text-center text-xl tracking-[0.4em] font-semibold h-12"
                  data-testid="delete-code-input"
                />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setMode('form')}>Cancel</Button>
                  <Button type="button" variant="destructive" onClick={confirmDelete} disabled={loading || deleteCode.length < 6} data-testid="delete-confirm-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Permanently delete'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MyTrips = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, isAuthenticated, loading: authLoading, logout, updateProfile } = useCustomerAuth();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming'); // Default to upcoming
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [changeBooking, setChangeBooking] = useState(null);
  const [completedTrips, setCompletedTrips] = useState([]);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);

  // Recurring service subscriptions — manage/cancel from the portal
  useEffect(() => {
    if (!customer?.id) return;
    fetch(`${API_URL}/api/customer/subscriptions`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.subscriptions) setSubscriptions(d.subscriptions); })
      .catch(() => {});
  }, [customer?.id]);

  // Completed trips power the Experience Passport + Flow Level
  useEffect(() => {
    if (!customer?.id) return;
    let cancelled = false;
    const load = (attempt = 0) => {
      fetch(`${API_URL}/api/customer/bookings?limit=100&status=completed`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => {
          if (cancelled) return;
          if (d?.bookings) setCompletedTrips(d.bookings);
          if (typeof d?.total === 'number') setCompletedTotal(d.total);
        })
        .catch(() => {
          if (!cancelled && attempt < 2) setTimeout(() => load(attempt + 1), 2000);
        });
    };
    load();
    return () => { cancelled = true; };
  }, [customer?.id]);

  const passportStamps = React.useMemo(() => {
    const map = {};
    completedTrips.forEach((b) => {
      if (!b.city) return;
      const key = `${b.city}|${b.country || ''}`;
      if (!map[key]) map[key] = { city: b.city, country: b.country || '', count: 0 };
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [completedTrips]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cities, setCities] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [country, setCountry] = useState(getMarketplaceCountry);
  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const searchRef = useRef(null);
  const tripsRef = useRef(null); // Ref for scrolling to trips section
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 10; // Load 10 at a time for better performance

  // Check if user data was passed from auth callback
  const userFromCallback = location.state?.user;
  
  // Scroll to trips section helper
  const scrollToTrips = () => {
    if (tripsRef.current) {
      tripsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Fetch cities for search dropdown (country-aware)
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${API_URL}/api/marketplace/cities?country=${country}`);
        if (response.ok) {
          const data = await response.json();
          setCities(data.cities || []);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };
    fetchCities();
  }, [country]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [avatarBroken, setAvatarBroken] = useState(false);

  // Redirect if not authenticated (skip if user data passed from callback)
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !userFromCallback) {
      navigate('/marketplace?signin=true');
    }
  }, [authLoading, isAuthenticated, navigate, userFromCallback]);

  // Fetch bookings - reset pagination when tab changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings(true); // Reset pagination on tab change
    }
  }, [isAuthenticated, activeTab]);

  const fetchBookings = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const currentOffset = reset ? 0 : offset;
      const statusParam = activeTab === 'all' ? '' : `&status=${activeTab}`;
      const response = await fetch(
        `${API_URL}/api/customer/bookings?limit=${LIMIT}&offset=${currentOffset}${statusParam}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        const newBookings = data.bookings || [];
        
        if (reset) {
          setBookings(newBookings);
        } else {
          setBookings(prev => [...prev, ...newBookings]);
        }
        
        setTotalCount(data.total || 0);
        setHasMore(newBookings.length === LIMIT && (currentOffset + newBookings.length) < (data.total || 0));
        setOffset(currentOffset + newBookings.length);

        // Self-heal: marketplace bookings paid via Stripe can sit 'pending' if the
        // success page was skipped — settle them against Stripe once, then refresh
        if (!window.__bfHealedPending) {
          const stale = newBookings.filter(
            (b) => b.status === 'pending' && (b.confirmation_code || '').startsWith('BM-')
          );
          if (stale.length > 0) {
            window.__bfHealedPending = true;
            Promise.allSettled(
              stale.slice(0, 6).map((b) =>
                fetch(`${API_URL}/api/v2/public/bookings/${b.confirmation_code}/confirm-payment`, { method: 'POST' })
              )
            ).then(() => fetchBookings(true));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchBookings(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const memberSince = customer?.member_since 
    ? new Date(customer.member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Recently';

  // Filter cities based on search input
  const filteredCities = cities.filter(city => 
    city.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (city.country && city.country.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md sticky top-0 z-50 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-9 w-9 object-contain rounded-lg"
              />
              <span className="text-xl font-bold text-gray-900 hidden sm:inline">BookingFlows</span>
            </Link>
            
            {/* Header Search Bar with Dropdown */}
            <div ref={searchRef} className="flex-1 max-w-xl hidden md:block relative">
              <form onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  navigate(`/search?country=${country.toLowerCase()}&q=${encodeURIComponent(searchQuery)}`);
                }
              }}>
                <div className="flex items-center gap-2 p-1.5 bg-white rounded-full border border-gray-200">
                  <div className="flex-1 flex items-center gap-2 pl-3">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSearchDropdown(true);
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      placeholder="Where to?"
                      className="flex-1 border-0 outline-none text-sm placeholder:text-gray-400 bg-transparent"
                      data-testid="header-search-input"
                    />
                  </div>
                  <div className="relative hidden sm:block">
                    <button
                      type="button"
                      onClick={() => setShowCountryMenu((v) => !v)}
                      data-testid="mytrips-country-btn"
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 border-l border-gray-200 transition-colors"
                    >
                      <span>{MARKETPLACE_COUNTRIES.find((c) => c.code === country)?.flag}</span>
                      <span>{country}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${showCountryMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showCountryMenu && (
                      <>
                        <div className="fixed inset-0 z-[90]" onClick={() => setShowCountryMenu(false)} />
                        <div className="absolute right-0 top-full mt-2 w-36 bg-white rounded-xl shadow-xl z-[100] py-1" data-testid="mytrips-country-menu">
                          {MARKETPLACE_COUNTRIES.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              data-testid={`mytrips-country-${c.code.toLowerCase()}`}
                              onClick={() => {
                                setCountry(c.code);
                                setMarketplaceCountry(c.code);
                                setShowCountryMenu(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${country === c.code ? 'font-semibold text-blue-600' : 'text-gray-700'}`}
                            >
                              <span>{c.flag}</span> {c.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <Button 
                    type="submit"
                    size="sm"
                    className="rounded-full px-4 text-xs bg-blue-600 hover:bg-blue-700"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* City Dropdown */}
              {showSearchDropdown && (filteredCities.length > 0 || cities.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl ring-1 ring-gray-100 z-[100]">
                  <div className="p-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {searchQuery ? 'Matching Cities' : 'Popular Destinations'}
                    </p>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {(searchQuery ? filteredCities : cities).slice(0, 5).map((city) => (
                      <button
                        key={city.city}
                        onClick={() => {
                          setShowSearchDropdown(false);
                          navigate(`/search?country=${country.toLowerCase()}&city=${encodeURIComponent(city.city)}`);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={getCityImage(city.city)}
                            alt={city.city}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{city.city}</p>
                          <p className="text-xs text-gray-500">{city.country}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">{city.operatorCount} {city.operatorCount === 1 ? 'provider' : 'providers'}</p>
                        </div>
                      </button>
                    ))}
                    {filteredCities.length === 0 && searchQuery && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <p>No destinations found for &quot;{searchQuery}&quot;</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 bg-gray-50">
                {customer?.avatar_url && !avatarBroken ? (
                  <img 
                    src={customer.avatar_url} 
                    alt={customer.name}
                    onError={() => setAvatarBroken(true)}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <span className="hidden sm:inline text-sm font-medium text-gray-700 pr-1">
                  {customer?.name?.split(' ')[0] || 'Profile'}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-600">
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Sign out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card - Left Sidebar */}
          <div className="lg:col-span-1">
            <Card 
              className="sticky top-24 rounded-3xl border-0 shadow-[0_8px_24px_rgba(0,0,0,0.05)]"
            >
              <CardContent className="pt-6">
                {/* Avatar */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {customer?.avatar_url && !avatarBroken ? (
                        <img 
                          src={customer.avatar_url} 
                          alt={customer.name}
                          onError={() => setAvatarBroken(true)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-12 h-12 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Name & Email */}
                  <h2 className="mt-4 text-xl font-semibold text-gray-900">
                    {customer?.name || 'Traveler'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {customer?.email}
                  </p>

                  {/* Edit button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4 rounded-full"
                    onClick={() => setEditModalOpen(true)}
                  >
                    <Edit2 className="w-3 h-3 mr-2" />
                    Edit profile
                  </Button>
                </div>

                {/* Stats - Clickable */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <button 
                      onClick={() => {
                        setActiveTab('upcoming');
                        scrollToTrips();
                      }}
                      className="p-3 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <p className="text-2xl font-bold text-gray-900 mb-1">
                        {customer?.total_trips || 0}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                        <Ticket className="w-3 h-3 text-blue-500" />
                        My Trips
                      </p>
                    </button>
                    <button 
                      onClick={() => {
                        setActiveTab('completed');
                        scrollToTrips();
                      }}
                      className="p-3 rounded-xl hover:bg-gray-100 transition-colors"
                      title="View trips you've reviewed"
                    >
                      <p className="text-2xl font-bold text-gray-900 mb-1">
                        {customer?.total_reviews || 0}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center justify-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        Reviews
                      </p>
                    </button>
                  </div>
                </div>

                {/* Flow Level (gamified, earned) — passport lives in Edit Profile */}
                <FlowLevel completedCount={completedTotal || completedTrips.length} />

                {/* Member since - simplified */}
                <div className="mt-4 pt-2">
                  <p className="text-xs text-gray-400 text-center">
                    Member since {memberSince}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bookings - Main Content */}
          <div className="lg:col-span-2" ref={tripsRef}>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">My Trips</h1>
              <p className="text-gray-500">View and manage your bookings</p>
            </div>

            {/* Subscriptions — recurring services tied to this customer's email */}
            {subscriptions.length > 0 && (
              <div className="mb-6" data-testid="mytrips-subscriptions">
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> My Subscriptions
                </h2>
                <div className="space-y-3">
                  {subscriptions.map((s) => (
                    <Card key={s.id} className="rounded-2xl shadow-sm" data-testid={`subscription-card-${s.id}`}>
                      <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {s.orgLogo ? (
                          <img src={s.orgLogo} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <RefreshCw className="w-4 h-4 text-blue-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">{s.serviceName}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {s.orgName ? `${s.orgName} · ` : ''}{s.currency} {Number(s.amount).toLocaleString()} / {s.interval}
                            {s.nextBillingDate ? ` · next billing ${s.nextBillingDate}` : ''}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                          s.cancelRequested && s.status !== 'cancelled' ? 'bg-amber-100 text-amber-700'
                            : s.status === 'active' ? 'bg-green-100 text-green-700'
                            : s.status === 'paused' ? 'bg-amber-100 text-amber-700'
                            : s.status === 'past_due' ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {s.cancelRequested && s.status !== 'cancelled' ? 'cancel requested' : s.status.replace('_', ' ')}
                        </span>
                        {s.status === 'active' && s.orgSlug && (
                          <Link to={`/members/${s.orgSlug}`} className="text-xs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap" data-testid={`subscription-arena-link-${s.id}`}>
                            Members Arena
                          </Link>
                        )}
                        {s.manageUrl && (
                          <a href={s.manageUrl} className="text-xs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap" data-testid={`subscription-manage-link-${s.id}`}>
                            Manage
                          </a>
                        )}
                      </div>
                      {s.cancelRequested && s.status !== 'cancelled' && (s.orgEmail || s.orgPhone) && (
                        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600" data-testid={`subscription-cancel-contact-${s.id}`}>
                          {s.orgName || 'The operator'} will confirm your cancellation. Questions? Contact them
                          {s.orgEmail && <> at <a href={`mailto:${s.orgEmail}`} className="text-blue-600 font-medium hover:underline">{s.orgEmail}</a></>}
                          {s.orgPhone && <>{s.orgEmail ? ' or ' : ' at '}<a href={`tel:${s.orgPhone}`} className="text-blue-600 font-medium hover:underline">{s.orgPhone}</a></>}
                        </div>
                      )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Tabs - Mobile friendly with horizontal scroll */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="grid w-full grid-cols-3 h-auto sm:inline-flex sm:w-auto" data-testid="mytrips-tabs">
                <TabsTrigger value="upcoming" className="text-xs sm:text-sm py-2 px-2 sm:px-4" data-testid="tab-upcoming">Upcoming</TabsTrigger>
                <TabsTrigger value="completed" className="text-xs sm:text-sm py-2 px-2 sm:px-4" data-testid="tab-completed">Completed</TabsTrigger>
                <TabsTrigger value="cancelled" className="text-xs sm:text-sm py-2 px-2 sm:px-4" data-testid="tab-cancelled">Cancelled</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Bookings list */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <div className="flex">
                      <Skeleton className="w-32 h-32" />
                      <div className="flex-1 p-4 space-y-3">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-4 w-1/3" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : bookings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Ticket className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No trips yet
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {activeTab === 'upcoming' 
                      ? "No upcoming trips. Time to book an adventure!"
                      : activeTab === 'completed'
                      ? "No completed trips yet."
                      : "No cancelled trips."}
                  </p>
                  <Link to="/">
                    <Button>
                      Explore experiences
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onReview={(b) => setReviewBooking(b)}
                    onChangeBooking={(b) => setChangeBooking(b)}
                    onViewTicket={async () => {
                      // Generate magic link for direct ticket access
                      const confirmationCode = booking.confirmation_code || booking.id;
                      try {
                        const response = await fetch(
                          `${API_URL}/api/v2/public/bookings/${confirmationCode}/generate-magic-link`,
                          { method: 'POST' }
                        );
                        const data = await response.json();
                        if (data.magicLink) {
                          // Navigate to the magic link URL
                          window.location.href = data.magicLink;
                        } else {
                          // Fallback to direct navigation
                          navigate(`/customer-ticket/${confirmationCode}`);
                        }
                      } catch (error) {
                        console.error('Failed to generate magic link:', error);
                        // Fallback to direct navigation
                        navigate(`/customer-ticket/${confirmationCode}`);
                      }
                    }}
                  />
                ))}
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="px-8"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Load More
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Total count indicator */}
                {bookings.length > 0 && (
                  <p className="text-center text-sm text-gray-500 pt-2">
                    Showing {bookings.length} of {totalCount} trips
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        customer={customer}
        passportStamps={passportStamps}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={updateProfile}
        onDeleted={async () => {
          setEditModalOpen(false);
          try { await logout(); } catch (e) { /* session already revoked */ }
          navigate('/marketplace');
        }}
      />

      {/* Review Modal (with photos) */}
      <ReviewModal
        booking={reviewBooking}
        open={!!reviewBooking}
        onClose={() => setReviewBooking(null)}
        onSubmitted={() => fetchBookings(true)}
      />

      {/* Change Booking Modal (operator contact) */}
      <ChangeBookingModal
        booking={changeBooking}
        open={!!changeBooking}
        onClose={() => setChangeBooking(null)}
      />

      {/* Footer - Simple version matching marketplace */}
      <footer className="py-12 px-4 bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-lg"
              />
              <span className="text-lg font-bold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
              <Link to="/support" className="hover:text-blue-600 transition-colors" data-testid="footer-support-link">Need Help?</Link>
              <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} BookingFlows
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MyTrips;
